import type { CardMetadata as importTypeCardMetadata } from '../types';
import client from './client';
import type { SearchResponse, ClassifyResponse, ChartPriceSearchResponse, CardIdentityResponse, MarketSummaryResponse } from '../types';

// 按 id_label 搜索销售记录
export async function searchSoldCards(
  idLabel: string,
  page: number = 1,
  pageSize: number = 10,
  platform?: string
): Promise<SearchResponse> {
  const response = await client.get<SearchResponse>(`/${idLabel}/sold-cards`, {
    params: {
      page,
      page_size: pageSize,
      platform: platform || undefined,
    },
  });
  return response.data;
}

export async function getCardIdentity(idLabel: string): Promise<CardIdentityResponse> {
  const response = await client.get<CardIdentityResponse>(`/${idLabel}/card-identity`);
  return response.data;
}

export async function getMarketSummary(idLabel: string): Promise<MarketSummaryResponse> {
  const response = await client.get<MarketSummaryResponse>(`/${idLabel}/market-summary`);
  return response.data;
}

export async function searchSoldCardPrices(
  idLabel: string,
  timeRange: 'all' | '1y' | '6m' | '3m' | '1m' = '3m'
): Promise<ChartPriceSearchResponse> {
  const response = await client.get<ChartPriceSearchResponse>(`/${idLabel}/search/sold-cards-price`, {
    params: {
      time_range: timeRange,
    },
  });
  return response.data;
}

// 图片识别卡片
export async function classifyImage(file: File): Promise<ClassifyResponse> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await client.post<ClassifyResponse>('/classify', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60000, // 图片识别可能较慢，延长超时时间
  });
  return response.data;
}

// 根据描述搜索卡片（返回候选卡片列表，支持分页）
export async function classifyByDescription(
  description: string,
  page: number = 1,
  pageSize: number = 20,
  filters: { language?: string; series?: string } = {}
): Promise<ClassifyResponse> {
  const response = await client.post<ClassifyResponse>('/classify/by_des', {
    ...filters,
    description,
    page,
    pageSize,
  });
  return response.data;
}

// 高级搜索
export async function advancedSearch(params: {
  description?: string;
  card_name?: string;
  platform?: string;
  min_price?: number;
  max_price?: number;
  currency?: string;
  language?: string;
  page?: number;
  page_size?: number;
}): Promise<SearchResponse> {
  const response = await client.get<SearchResponse>('/search/advanced', {
    params,
  });
  return response.data;
}

// 热门卡片响应类型
export interface HotCardItem extends importTypeCardMetadata {
  latest_sale?: { price: number; currency: string; sold_time: number; platform: string;
    grade_agency: string | null; grade_score: string | null } | null;
  id: string;
  name: string;
  image: string;
  number: string;
  transactionCount: number;
  avgPrice: number;
  avgPriceUsd?: number | null;
  pricedCount?: number;
  priceCoverageComplete?: boolean;
  missingCurrencyCount?: number;
  currency: string;
  fxAsOf?: string | null;
}

export interface HotCardsResponse {
  code: number;
  message: string;
  data: HotCardItem[];
  updatedAt?: string | null;
}

// 获取热门卡片
export async function getHotCards(
  limit: number = 100
): Promise<HotCardsResponse> {
  const response = await client.get<HotCardsResponse>('/hot-cards', {
    params: { limit },
  });
  return response.data;
}

export async function getMetadataFilters(): Promise<{languages: string[]; series: string[]}> {
  const response = await client.get('/cards/metadata-filters');
  return response.data.data;
}
